import React, { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { Download } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useGetData } from "@/hooks/useGetData";
import LoaderWrapper from "@/pages/LoaderWrapper";
import { patientV2LoaderWrapper } from "../shared/PatientV2CircularLoader";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import ReactSelect from "react-select";
import { toast } from "sonner";
import AxiosInstance from "@/utils/AxiosInstance";
import { getPromise, getPromiseToast } from "@/utils/helpers";
import { PatientDevicesList } from "./PatientDevicesList";
import BloodPressureReadings from "../readings/BloodPressureReadingsV2";
import BloodGlucoseReadings from "../readings/BloodGlucoseReadingsV2";
import BloodOxygenReadings from "../readings/BloodOxygenReadingsV2";
import BodyCompositionReadings from "../readings/BodyCompositionReadingsV2";
import PulseRateReadings from "../readings/PulseRateReadingsV2";
import type { DeviceV2 } from "../types";
import {
  exportPatientDeviceTabXlsx,
  type DeviceTabExportKey,
} from "@/utils/exportPatientDeviceTabXlsx";
import moment from "moment";
import type { PatientReadingTab } from "./deviceTabConfig";
import { DEVICE_TAB_CONFIG } from "./deviceTabConfig";

type DateRangeYmd = { startDate: string; endDate: string };

interface PatientDevicesReadingsProps {
  patient: any;
  devices?: DeviceV2[];
  hideListTab?: boolean;
  isPractitioner?: boolean;
  /** When true (e.g. patient on /patient-device), show only tabs for devices assigned to this patient */
  showOnlyAssignedDeviceTabs?: boolean;
  /** Incremented from parent (e.g. after /reading/sync/all) to refetch readings + devices */
  readingsRefreshNonce?: number;
  activeTab?: string;
  onActiveTabChange?: (tab: string) => void;
  /** Hide in-content tab list when sub-tab pills are rendered in PatientTabs header */
  hideTabList?: boolean;
  /** Hide in-content XLSX button; parent renders it in the sub-tab header */
  hideHeaderExport?: boolean;
  onExportControlsChange?: (controls: {
    visible: boolean;
    loading: boolean;
    onExport: () => void;
  } | null) => void;
  visibleTabs: PatientReadingTab[];
  devicesTimezone?: string;
  devicesLoading?: boolean;
  devicesReady?: boolean;
  onRefreshDevices?: () => Promise<void>;
  onRefreshReadingCount?: () => void;
}

interface Reading {
  _id: string;
  deviceType: string;
  readingDate: string;
  readingDateLocal?: string;
  glucose?: number;
  glucoseUnit?: string;
  systolic?: number;
  diastolic?: number;
  pulse?: number;
  spo2?: number;
  weight?: number;
  weightUnit?: string;
  status: string;
  isAbnormal: boolean;
  [key: string]: any;
}

const tabConfig = DEVICE_TAB_CONFIG;

const TAB_SWITCH_LOADER_SECONDS = 4;

export const PatientDevicesReadings: React.FC<PatientDevicesReadingsProps> = ({
  patient,
  devices: initialDevices = [],
  hideListTab,
  isPractitioner = false,
  showOnlyAssignedDeviceTabs,
  readingsRefreshNonce = 0,
  activeTab: activeTabProp,
  onActiveTabChange,
  hideTabList = false,
  hideHeaderExport = false,
  onExportControlsChange,
  visibleTabs,
  devicesTimezone = "",
  devicesLoading = false,
  devicesReady = false,
  onRefreshDevices,
  onRefreshReadingCount,
}) => {
  const [internalActiveTab, setInternalActiveTab] = useState("device");
  const activeTab = activeTabProp ?? internalActiveTab;
  const setActiveTab = onActiveTabChange ?? setInternalActiveTab;
  const [tabSwitchLoader, setTabSwitchLoader] = useState(true);
  const tabSwitchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasInitialLoaderRun = useRef(false);
  const [isMapDeviceDialogOpen, setIsMapDeviceDialogOpen] = useState(false);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>("");
  const [btnLoading, setBtnLoading] = useState(false);
  const [deviceExportLoading, setDeviceExportLoading] = useState(false);
  const patientId = patient?._id;
  const fetchReadingsRef = useRef<(() => void) | null>(null);

  const [tabDateRanges, setTabDateRanges] = useState<
    Partial<Record<string, DateRangeYmd>>
  >({});

  const handleReadingTabDateRangeChange = useCallback(
    (tabValue: string, range: DateRangeYmd) => {
      setTabDateRanges((p) => ({ ...p, [tabValue]: range }));
    },
    [],
  );

  /** Stable `(range) => void` per tab so reading components can safely depend on `onDateRangeChange`. */
  const readingTabDateRangeByTabValue = useMemo(() => {
    const map: Partial<Record<string, (range: DateRangeYmd) => void>> = {};
    for (const t of tabConfig) {
      if (t.isDeviceTab) continue;
      const tabValue = t.value;
      map[tabValue] = (range: DateRangeYmd) =>
        handleReadingTabDateRangeChange(tabValue, range);
    }
    return map;
  }, [handleReadingTabDateRangeChange]);

  const applyDateFilterForExport = useCallback(
    <T extends { readingDate?: string; readingDateLocal?: string }>(
      rows: T[],
      range?: DateRangeYmd,
    ) => {
      if (!range?.startDate || !range?.endDate) return rows;
      const rangeStart = moment(range.startDate, "YYYY-MM-DD").startOf("day");
      const rangeEnd = moment(range.endDate, "YYYY-MM-DD").endOf("day");
      return (rows || []).filter((r: any) => {
        const dt = r?.readingDateLocal || r?.readingDate;
        if (!dt) return false;
        const m = moment(dt);
        return m.isBetween(rangeStart, rangeEnd, undefined, "[]");
      });
    },
    [],
  );

  const handleTabChange = (value: string) => {
    const isReadingTab = !tabConfig.find((t) => t.value === value)?.isDeviceTab;
    if (isReadingTab && value !== activeTab) {
      if (tabSwitchTimerRef.current) clearTimeout(tabSwitchTimerRef.current);
      setTabSwitchLoader(true);
      setActiveTab(value);
      tabSwitchTimerRef.current = setTimeout(() => {
        setTabSwitchLoader(false);
        tabSwitchTimerRef.current = null;
      }, TAB_SWITCH_LOADER_SECONDS * 1000);
    } else {
      setActiveTab(value);
    }
  };

  // 5 sec loader on first/default tab render
  useEffect(() => {
    if (hasInitialLoaderRun.current) return;
    hasInitialLoaderRun.current = true;
    const t = setTimeout(() => setTabSwitchLoader(false), TAB_SWITCH_LOADER_SECONDS * 1000);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    return () => {
      if (tabSwitchTimerRef.current) clearTimeout(tabSwitchTimerRef.current);
    };
  }, []);

  const currentTab = tabConfig.find((tab) => tab.value === activeTab);
  const currentDeviceType = currentTab?.deviceType;
  const isReadingTab = currentDeviceType && !currentTab?.isDeviceTab && !currentTab?.isPulseRateTab;

  // Fetch readings data with deviceType query parameter for reading tabs
  const {
    apiData: readingsData,
    loading: readingsLoading1,
    err: readingsErr,
    fetchData: fetchReadings,
  } = useGetData(
    `/devices/patient/${patientId}/readings`,
    currentDeviceType ? { deviceType: currentDeviceType } : {},
    false,
    !!patientId && !!isReadingTab,
    () => { }
  );
  
  const readingsLoading = readingsLoading1;
  // Store fetchReadings in ref to avoid dependency issues
  fetchReadingsRef.current = fetchReadings;

  const readings: Reading[] = readingsData?.data?.readings || [];

  // Refresh readings after adding manually
  const refreshReadings = useCallback(() => {
    if (patientId && fetchReadingsRef.current) {
      fetchReadingsRef.current();
    }
  }, [patientId]);

  const handleReadingInvalidUpdated = useCallback(() => {
    refreshReadings();
    onRefreshReadingCount?.();
  }, [refreshReadings, onRefreshReadingCount]);

  useEffect(() => {
    if (!readingsRefreshNonce) return;
    refreshReadings();
    void onRefreshDevices?.();
  }, [readingsRefreshNonce, refreshReadings, onRefreshDevices]);

  // Fetch devices for mapping (available devices)
  const {
    apiData: devicesData,
    loading: catalogDevicesLoading,
    err: devicesErr,
    fetchData: fetchDevices,
  } = useGetData(
    "/devices",
    {},
    false,
    isMapDeviceDialogOpen, // Only fetch when dialog is open
    () => {}
  );

  const devices: any[] = devicesData?.data?.devices || [];

  // Convert devices to react-select options format
  const deviceOptions = devices.map((device) => ({
    value: device._id,
    label: device?.imei ? `${device.imei} (${device.deviceName})` : device.deviceName,
  }));

  const handleDownloadDeviceTab = async () => {
    if (!patientId) {
      toast.error("Patient ID is missing");
      return;
    }
    setDeviceExportLoading(true);
    try {
      const tabMeta = tabConfig.find((t) => t.value === activeTab);
      const tabLabel = tabMeta?.name || activeTab;
      const selectedRange = tabDateRanges[activeTab];

      if (activeTab === "device") {
        exportPatientDeviceTabXlsx(
          patient,
          "device",
          tabLabel,
          initialDevices,
          [],
          patientId,
          activeTab,
        );
      } else if (activeTab === "blood_pressure") {
        // Use already-loaded BP readings (no refetch) so export matches the UI table/date range.
        const readingsRaw = (readings || []).filter(
          (reading) =>
            !reading?.invalid &&
            (!tabMeta?.deviceType || reading.deviceType === tabMeta.deviceType),
        );
        
        const filteredReadings = applyDateFilterForExport(
          readingsRaw as any[],
          selectedRange,
        );
        exportPatientDeviceTabXlsx(
          patient,
          "blood_pressure",
          tabLabel,
          initialDevices,
          filteredReadings,
          patientId,
          activeTab,
        );
      } else if (activeTab === "pulse_rate") {
        const { data: res } = await AxiosInstance().get<{
          data?: { readings?: unknown[] } | unknown[];
        }>("/reading/pulse", {
          params: { patientId },
        });
        const inner = res?.data;
        const readingsRaw = Array.isArray(inner)
          ? inner
          : inner && typeof inner === "object" && "readings" in inner
            ? (inner as { readings?: unknown[] }).readings ?? []
            : [];
        const readings = applyDateFilterForExport(readingsRaw?.filter((reading: any) => !reading?.invalid) as any[], selectedRange);
        exportPatientDeviceTabXlsx(
          patient,
          "pulse_rate",
          tabLabel,
          initialDevices,
          readings,
          patientId,
          activeTab,
        );
      } else if (tabMeta?.deviceType) {
        const { data: res } = await AxiosInstance().get<{
          data?: { readings?: unknown[] };
        }>(`/devices/patient/${patientId}/readings`, {
          params: {
            deviceType: tabMeta.deviceType,
            limit: 10000,
            page: 1,
            skip: 0,
          },
        });
        const readingsRaw = (res?.data?.readings ?? []) as any[];
        console.log("readingsRaw", readingsRaw);
        const readings = applyDateFilterForExport(readingsRaw?.filter((reading: any) => !reading?.invalid) as any[], selectedRange);
        console.log("readings", readings);
        exportPatientDeviceTabXlsx(
          patient,
          activeTab as DeviceTabExportKey,
          tabLabel,
          initialDevices,
          readings,
          patientId,
          activeTab,
        );
      } else {
        toast.error("Nothing to export for this tab");
        return;
      }
      toast.success("Downloaded");
    } catch {
      toast.error("Could not export. Try again.");
    } finally {
      setDeviceExportLoading(false);
    }
  };

  const exportHandlerRef = useRef(handleDownloadDeviceTab);
  exportHandlerRef.current = handleDownloadDeviceTab;
  const lastExportStateRef = useRef<{ visible: boolean; loading: boolean } | null>(null);

  useEffect(() => {
    if (!hideHeaderExport || !onExportControlsChange) return;

    const visible = activeTab !== "device";
    const last = lastExportStateRef.current;
    if (last?.visible === visible && last?.loading === deviceExportLoading) {
      return;
    }
    lastExportStateRef.current = { visible, loading: deviceExportLoading };

    onExportControlsChange({
      visible,
      loading: deviceExportLoading,
      onExport: () => exportHandlerRef.current(),
    });

    return () => {
      lastExportStateRef.current = null;
      onExportControlsChange(null);
    };
  }, [activeTab, deviceExportLoading, hideHeaderExport, onExportControlsChange]);

  const handleMapDevice = async () => {
    if (!selectedDeviceId) {
      toast.error("Please select a device");
      return;
    }

    if (!patientId) {
      toast.error("Patient ID is missing");
      return;
    }

    const { pr, rejectFn, resolveFn } = getPromise();
    getPromiseToast(pr);

    try {
      setBtnLoading(true);
      const { data } = await AxiosInstance().post("/devices/map", {
        deviceId: selectedDeviceId,
        patientId: patientId,
      });

      resolveFn("Device mapped successfully");
      setIsMapDeviceDialogOpen(false);
      setSelectedDeviceId("");
      await onRefreshDevices?.();
    } catch (error: any) {
      rejectFn(error);
      toast.error(
        error?.response?.data?.message || "Failed to map device"
      );
    } finally {
      setBtnLoading(false);
    }
  };

  return (
    <div className="teachers min-w-0">
      <div className="min-w-0">
        {/* <h2 className="flex items-center gap-2 teachers font-bold text-xl mb-4">
          List
        </h2> */}











        <Tabs value={activeTab} onValueChange={handleTabChange}>
          {!hideTabList || (activeTab !== "device" && !hideHeaderExport) ? (
          <div className="mb-2 flex flex-col gap-2 md:mb-4 md:overflow-x-auto">
            {!hideTabList ? (
              <TabsList className="h-10 w-full shrink-0 flex-wrap rounded-lg border border-grey-light bg-grey-light p-0 md:w-auto">
                {visibleTabs.map((tab) => {
                  const IconComponent = tab.icon;
                  const isSelected = activeTab === tab.value;
                  return (
                    <TabsTrigger
                      key={tab.value}
                      value={tab.value}
                      title={tab.name}
                      className="teachers flex items-center gap-2 rounded-lg px-3 text-sm data-[state=active]:bg-primary data-[state=active]:px-4 data-[state=active]:text-white data-[state=active]:shadow-none"
                    >
                      {IconComponent ? <IconComponent className="h-4 w-4 shrink-0" /> : null}
                      {isSelected ? <span>{tab.name}</span> : null}
                    </TabsTrigger>
                  );
                })}
              </TabsList>
            ) : null}
            {activeTab !== "device" && !hideHeaderExport ? (
              <div className="flex justify-end">
                <Button
                  type="button"
                  variant="outline"
                  className="shrink-0"
                  disabled={deviceExportLoading || !patientId}
                  onClick={handleDownloadDeviceTab}
                >
                  <Download className="mr-2 h-4 w-4" />
                  {deviceExportLoading ? "Exporting…" : "XLSX"}
                </Button>
              </div>
            ) : null}
          </div>
          ) : null}
          {visibleTabs.map((tab) => {
            // If it's the Device tab, show device component
            if (tab.isDeviceTab) {
              return (
                <TabsContent key={tab.value} value={tab.value} className="mt-4">
                  <LoaderWrapper
                    loading={tabSwitchLoader || devicesLoading}
                    error={null}
                    customLoaderComponent={patientV2LoaderWrapper()}
                  >
                    <PatientDevicesList
                      patient={patient}
                      devices={initialDevices}
                      resolvedTimezone={devicesTimezone}
                      onRefresh={onRefreshDevices}
                    />
                  </LoaderWrapper>
                </TabsContent>
              );
            }

            // Handle Pulse Rate tab separately (uses different API)
            if (tab.isPulseRateTab) {
              return (
                <TabsContent key={tab.value} value={tab.value} className="mt-4">
                  <LoaderWrapper loading={tabSwitchLoader} error={null} customLoaderComponent={patientV2LoaderWrapper()}>
                    <PulseRateReadings
                      patientId={patientId as string}
                      onDateRangeChange={readingTabDateRangeByTabValue[tab.value]}
                      onReadingInvalidUpdated={handleReadingInvalidUpdated}
                    />
                  </LoaderWrapper>
                </TabsContent>
              );
            }

            // Filter readings for this specific tab
            const tabReadings = readings.filter(
              (r) => r.deviceType === tab.deviceType
            );
            const tabDeviceType = tab.deviceType;

            return (
              <TabsContent key={tab.value} value={tab.value} className="mt-0 md:mt-4">
                <LoaderWrapper loading={tabSwitchLoader || readingsLoading} error={readingsErr} customLoaderComponent={patientV2LoaderWrapper()}>
                  {tabDeviceType === "blood_pressure" && (
                    <BloodPressureReadings
                      readings={tabReadings}
                      loading={readingsLoading}
                      error={readingsErr}
                      patientId={patientId as string}
                      patient={patient}
                      patientDevices={initialDevices}
                      onReadingAdded={refreshReadings}
                      onReadingInvalidUpdated={handleReadingInvalidUpdated}
                      onDateRangeChange={readingTabDateRangeByTabValue[tab.value]}
                    />
                  )}
                  {tabDeviceType === "glucose_meter" && (
                    <BloodGlucoseReadings
                      readings={tabReadings}
                      loading={readingsLoading}
                      error={readingsErr}
                      patientId={patientId as string}
                      patient={patient}
                      patientDevices={initialDevices}
                      onReadingAdded={refreshReadings}
                      onReadingInvalidUpdated={handleReadingInvalidUpdated}
                      onDateRangeChange={readingTabDateRangeByTabValue[tab.value]}
                    />
                  )}
                  {tabDeviceType === "pulse_oximeter" && (
                    <BloodOxygenReadings
                      readings={tabReadings}
                      loading={readingsLoading}
                      error={readingsErr}
                      patientId={patientId as string}
                      patient={patient}
                      patientDevices={initialDevices}
                      onReadingAdded={refreshReadings}
                      onReadingInvalidUpdated={handleReadingInvalidUpdated}
                      onDateRangeChange={readingTabDateRangeByTabValue[tab.value]}
                    />
                  )}
                  {tabDeviceType === "weight_scale" && (
                    <BodyCompositionReadings
                      readings={tabReadings}
                      loading={readingsLoading}
                      error={readingsErr}
                      patientId={patientId as string}
                      patient={patient}
                      patientDevices={initialDevices}
                      onReadingAdded={refreshReadings}
                      onReadingInvalidUpdated={handleReadingInvalidUpdated}
                      onDateRangeChange={readingTabDateRangeByTabValue[tab.value]}
                    />
                  )}
                </LoaderWrapper>
              </TabsContent>
            );
          })}
        </Tabs>

        {/* Map Device Dialog */}
        <Dialog
          open={isMapDeviceDialogOpen}
          onOpenChange={(open) => {
            setIsMapDeviceDialogOpen(open);
            if (!open) {
              setSelectedDeviceId("");
            }
          }}
        >
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Map Device</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <LoaderWrapper loading={catalogDevicesLoading} error={devicesErr} customLoaderComponent={patientV2LoaderWrapper()}>
                <div className="space-y-2">
                  <Label htmlFor="device-select">
                    Select Device <span className="text-destructive">*</span>
                  </Label>
                  <ReactSelect
                    id="device-select"
                    options={deviceOptions}
                    value={deviceOptions.find((opt) => opt.value === selectedDeviceId) || null}
                    onChange={(selected) => {
                      setSelectedDeviceId(selected?.value || "");
                    }}
                    placeholder="Select a device"
                    isSearchable={true}
                    isClearable={false}
                    className="w-full"
                    classNamePrefix="device-select"
                    styles={{
                      control: (base) => ({
                        ...base,
                        minHeight: "40px",
                        borderColor: "#e5e7eb",
                        "&:hover": {
                          borderColor: "#d1d5db",
                        },
                      }),
                      menu: (base) => ({
                        ...base,
                        zIndex: 9999,
                      }),
                    }}
                    noOptionsMessage={() => "No devices available"}
                  />
                </div>
              </LoaderWrapper>
              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsMapDeviceDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  disabled={btnLoading || !selectedDeviceId}
                  onClick={handleMapDevice}
                >
                  Map
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};






